/**
 * 项目权限配置 API
 */
import apiClient from './config';

export interface ProjectRolePermission {
  role: string;
  permission_code: string;
  is_enabled: boolean;
  updated_at: string;
}

export interface RolePermissionsResponse {
  role: string;
  permissions: string[];
  updated_at: string;
}

export interface BatchProjectPermissionsResponse {
  [projectId: string]: Record<string, boolean>;
}

/**
 * 获取所有项目权限配置
 */
export async function getAllProjectPermissions(): Promise<RolePermissionsResponse[]> {
  const response = await apiClient.get('/admin/project-permissions');
  return response.data;
}

/**
 * 获取指定角色的权限配置
 */
export async function getRolePermissions(role: string): Promise<RolePermissionsResponse> {
  const response = await apiClient.get(`/admin/project-permissions/${role}`);
  return response.data;
}

/**
 * 更新角色权限配置
 */
export async function updateRolePermissions(
  role: string,
  permissions: string[]
): Promise<void> {
  await apiClient.put(`/admin/project-permissions/${role}`, {
    permissions,
  });
}

/**
 * 获取默认权限配置
 */
export async function getDefaultPermissions(): Promise<Record<string, string[]>> {
  const response = await apiClient.get('/admin/project-permissions/default');
  return response.data;
}

/**
 * 检查用户在指定项目中是否有指定权限
 */
export async function checkProjectPermission(
  projectId: string,
  permissionCode: string
): Promise<boolean> {
  const response = await apiClient.post('/user/project-permissions/check', {
    project_id: projectId,
    permission_code: permissionCode,
  });
  return response.data?.has_permission || false;
}

/**
 * 批量检查用户在指定项目中的多个权限
 * @param projectId 项目ID
 * @param permissionCodes 权限代码数组
 * @returns 权限映射对象 { [permissionCode]: boolean }
 */
export async function checkProjectPermissions(
  projectId: string,
  permissionCodes: string[]
): Promise<Record<string, boolean>> {
  if (permissionCodes.length === 0) {
    return {};
  }

  const response = await apiClient.post('/user/project-permissions/check-batch', {
    project_id: projectId,
    permission_codes: permissionCodes,
  });
  return response.data?.permissions || {};
}

/**
 * 批量检查多个项目的多个权限
 * @param projectIds 项目ID数组
 * @param permissionCodes 权限代码数组
 * @returns 权限映射对象 { [projectId]: { [permissionCode]: boolean } }
 */
export async function checkProjectPermissionsBatch(
  projectIds: string[],
  permissionCodes: string[]
): Promise<BatchProjectPermissionsResponse> {
  if (projectIds.length === 0 || permissionCodes.length === 0) {
    return {};
  }

  const response = await apiClient.post('/user/project-permissions/batch', {
    project_ids: projectIds,
    permission_codes: permissionCodes,
  });

  return response.data || {};
}
